import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'flex justify-center items-center gap-2 transition-colors font-medium leading-tight disabled:cursor-not-allowed cursor-pointer',
  {
    variants: {
      variant: {
        primary:
          'bg-blue-800 text-white hover:bg-blue-700 disabled:opacity-40 disabled:bg-blue-500 active:bg-blue-950',
        primaryClear:
          'outline outline-1 outline-offset-[-1px] outline-blue-800 text-blue-800 hover:outline-blue-700 hover:text-blue-700 active:outline-blue-950 active:text-blue-950 disabled:opacity-40 disabled:outline-blue-500',
        secondary:
          'bg-blue-500 text-white hover:bg-blue-400 active:bg-blue-700 disabled:opacity-40 disabled:bg-blue-200 disabled:text-blue-600',
        secondaryClear:
          'outline outline-1 outline-offset-[-1px] outline-blue-300 text-blue-600 hover:outline-blue-200 hover:text-blue-500 focus:outline-[6px] focus:outline-blue-300 focus:text-blue-500 active:outline-blue-700 active:text-blue-500 active:outline-1 disabled:outline-blue-100 disabled:opacity-40 disabled:text-blue-500',
        clear:
          'text-gray-800 hover:bg-gray-100 hover:text-blue-700 active:bg-blue-200 disabled:opacity-40',
        outline:
          'rounded-xl outline outline-1 outline-offset-[-1px] text-blue-900 outline-blue-100 hover:text-grey-500 hover:outline-grey-500 active:outline-blue-600 active:text-blue-600 disabled:opacity-40 disabled:text-blue-900',
        destructive:
          'bg-red-600 text-white hover:bg-red-400 focus:outline focus:outline-[6px] focus:outline-red-300 active:outline-0 active:bg-red-700 disabled:opacity-40',
        destructiveClear:
          'outline outline-1 outline-offset-[-1px] outline-red-600 text-red-600 hover:outline-red-400 focus:outline focus:outline-[6px] focus:outline-red-300 active:outline-1 active:outline-red-700 active:text-red-700 disabled:opacity-40',
        overlayAction:
          'bg-black/50 backdrop-blur-md text-white hover:bg-black/70 active:bg-black/80 rounded-lg shadow-lg border border-white/10',
      },
      size: {
        sm: 'h-8 px-3 py-2 text-xs rounded-lg',
        md: 'h-12 px-6 py-3 text-sm rounded-2xl',
        lg: 'h-14 px-8 py-4 text-base rounded-2xl',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  submit?: boolean;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  wrapChildren?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  leftIcon,
  rightIcon,
  children,
  submit = false,
  disabled = false,
  loading = false,
  loadingText = 'Cargando...',
  wrapChildren = true,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      type={submit ? 'submit' : 'button'}
      disabled={disabled || loading}
      {...props}
    >
      {leftIcon && <span className="overflow-hidden">{leftIcon}</span>}
      {(loading || children) &&
        (wrapChildren ? (
          <span className="align-middle">{loading ? loadingText : children}</span>
        ) : (
          children
        ))}
      {rightIcon && <span className="overflow-hidden">{rightIcon}</span>}
    </Comp>
  );
}

export { Button, buttonVariants };
